import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ApiError } from "../../api/client";
import type { LotOption, LotOptions } from "../../api/dispatchOrders";
import type { Order, OrderLine } from "../../api/orders";
import { warehouseApi, type HeldLot, type OrderHolds, type Reservation } from "../../api/warehouse";
import { useAuth } from "../../auth/AuthContext";
import { formatDate, formatDateTime, formatNumber } from "../../lib/format";
import { LotExpiry } from "../LotExpiry";
import { LotQualityBadge } from "../LotQualityBadge";
import { useToast } from "../Toast";
import { card, cardHead, cardTitle, hint, item, muted, mono, nested, row } from "./orderCardStyles";

type T = ReturnType<typeof useTranslation>["t"];

/** Artikal porudžbine: njene stavke tog ERP artikla, sabrane. */
type Artikal = { code: string; name: string | null; ordered: number; unit: string };

/**
 * Rezervisana roba na strani porudžbine, za zaposlene: koji LOT-ovi se u magacinu drže za nju, po artiklu, i koliko od
 * naručenog to pokriva. Drži se rezervacijom (samo u portalu; ERP je ne vidi) ili LOT-om na nalogu za otpremu koji još
 * nije otišao; za istu porudžbinu važi veće od ta dva, kao u Magacinu.
 *
 * Ko sme da drži robu za kupca (prodaja za svoje kupce, direktor, kvalitet), rezerviše odavde: LOT-ovi artikla sa
 * stanja, prvo onaj koji pre ističe, kako ih Magacin nudi i nalogu.
 */
export function OrderReservations({ order }: { order: Order }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { hasPermission } = useAuth();
  const toast = useToast();
  const mozeSledljivost = hasPermission("section:traceability:read");
  const [holds, setHolds] = useState<OrderHolds | null>(null);
  const [greska, setGreska] = useState(false);
  const [osvezi, setOsvezi] = useState(0);
  // Artikal kome je otvoren izbor LOT-a.
  const [bira, setBira] = useState<string | null>(null);
  const [radi, setRadi] = useState<string | null>(null);

  useEffect(() => {
    let vazi = true;
    warehouseApi
      .orderHolds(order.id)
      .then((h) => {
        if (vazi) {
          setHolds(h);
          setGreska(false);
        }
      })
      .catch(() => {
        if (vazi) setGreska(true);
      });
    return () => {
      vazi = false;
    };
  }, [order.id, osvezi]);

  const mozeRezervisati = !!holds?.mayReserve && hasPermission("stock_reservations:manage");
  const artikli = poArtiklu(order.lines);
  const kolicina = (q: number, unit: string | null | undefined) =>
    `${formatNumber(q, lang, { maximumFractionDigits: 3 })} ${unit ?? ""}`.trim();

  async function otkazi(r: Reservation) {
    if (!r.id) return;
    setRadi(r.id);
    try {
      await warehouseApi.cancelReservation(r.id);
      toast.success(t("warehouse.reservationCancelled", "Rezervacija je otkazana."));
      setOsvezi((n) => n + 1);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(null);
    }
  }

  if (greska) {
    return (
      <section style={card}>
        <h2 style={cardTitle}>{t("orderHolds.title", "Rezervisana roba")}</h2>
        <p style={hint}>{t("orderHolds.failed", "Rezervacije nisu mogle da se učitaju.")}</p>
      </section>
    );
  }
  // Ništa se ne drži i ništa ne sme da se rezerviše: kartica nema šta da kaže.
  if (!holds || (!mozeRezervisati && holds.lots.length === 0)) return null;

  return (
    <section style={card} aria-label={t("orderHolds.title", "Rezervisana roba")}>
      <header style={cardHead}>
        <h2 style={cardTitle}>{t("orderHolds.title", "Rezervisana roba")}</h2>
      </header>
      <p style={hint}>
        {t("orderHolds.hint", "Roba iz magacina koja se drži za ovu porudžbinu. Rezervacija postoji samo u portalu; ERP je ne vidi.")}
      </p>
      <ul style={lista}>
        {artikli.map((a) => {
          const lotovi = holds.lots.filter((l) => l.erpProductId === a.code);
          const drzi = lotovi.reduce((n, l) => n + l.held, 0);
          // Ono što je otišlo više ne mora da se drži: pokriva porudžbinu zajedno sa onim što se drži.
          const otislo = holds.shipped?.[a.code] ?? 0;
          const pokriveno = drzi + otislo;
          const boja = pokriveno >= a.ordered ? "ok" : pokriveno > 0 ? "warn" : "";
          return (
            <li key={a.code} style={item}>
              <div style={row}>
                <span style={{ color: "var(--ink)", fontWeight: 600 }}>{a.name ?? a.code}</span>
                <span style={muted}>{t("warehouse.code", "šifra {{code}}", { code: a.code })}</span>
                <span className={boja ? `tag ${boja}` : "tag"} style={{ marginLeft: "auto" }}>
                  {otislo > 0
                    ? t("orderHolds.heldShipped", "otpremljeno {{shipped}} · drži se {{held}} od {{ordered}}", {
                        shipped: formatNumber(otislo, lang, { maximumFractionDigits: 3 }),
                        held: formatNumber(drzi, lang, { maximumFractionDigits: 3 }),
                        ordered: kolicina(a.ordered, a.unit),
                      })
                    : t("orderHolds.held", "drži se {{held}} od {{ordered}}", {
                        held: formatNumber(drzi, lang, { maximumFractionDigits: 3 }),
                        ordered: kolicina(a.ordered, a.unit),
                      })}
                </span>
              </div>
              {lotovi.length > 0 && (
                <div style={nested}>
                  {lotovi.map((l) => (
                    <DrzaniLot
                      key={l.goodsLotId}
                      l={l}
                      unit={a.unit}
                      lang={lang}
                      t={t}
                      mozeSledljivost={mozeSledljivost}
                      radi={radi}
                      onCancel={(r) => void otkazi(r)}
                    />
                  ))}
                </div>
              )}
              {mozeRezervisati &&
                (bira === a.code ? (
                  <IzborLota
                    orderId={order.id}
                    artikal={a}
                    drzi={pokriveno}
                    onDone={() => {
                      setBira(null);
                      setOsvezi((n) => n + 1);
                    }}
                    onCancel={() => setBira(null)}
                  />
                ) : (
                  <div>
                    <button type="button" className="btn sm" onClick={() => setBira(a.code)}>
                      {t("orderHolds.reserve", "Rezerviši")}
                    </button>
                  </div>
                ))}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Jedan LOT koji se drži za porudžbinu, i ispod njega kako: rezervacija (ko, kada, napomena) ili nalog za otpremu. */
function DrzaniLot({
  l,
  unit,
  lang,
  t,
  mozeSledljivost,
  radi,
  onCancel,
}: {
  l: HeldLot;
  unit: string;
  lang?: string;
  t: T;
  mozeSledljivost: boolean;
  radi: string | null;
  onCancel: (r: Reservation) => void;
}) {
  const kolicina = (q: number) => `${formatNumber(q, lang, { maximumFractionDigits: 3 })} ${unit}`.trim();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={row}>
        <span style={mono}>
          {mozeSledljivost ? <Link to={`/traceability?lot=${l.goodsLotId}`}>{l.lot}</Link> : l.lot}
        </span>
        <span style={muted}>
          <LotExpiry iso={l.expiresOn} lang={lang} />
        </span>
        <LotQualityBadge status={l.qualityStatus} note={l.qualityNote} />
        <b style={{ marginLeft: "auto", color: "var(--ink)", whiteSpace: "nowrap" }}>{kolicina(l.held)}</b>
      </div>
      {l.entries.map((e, i) => (
        <div key={e.id ?? `${e.dispatchOrderId}-${i}`} style={{ ...row, ...muted, paddingLeft: 12 }}>
          <span style={{ whiteSpace: "nowrap" }}>{kolicina(e.quantity)}</span>
          {e.kind === "DISPATCH_ORDER" ? (
            <span className="tag info">
              {e.dispatchOrderNumber
                ? t("warehouse.onDispatch", "na nalogu {{number}}", { number: e.dispatchOrderNumber })
                : t("warehouse.onDispatchNoNumber", "na nalogu za otpremu")}
            </span>
          ) : (
            <span>
              {t("orderHolds.reservation", "rezervacija")}
              {[e.createdBy, e.createdAt ? formatDate(e.createdAt, lang) : null].filter(Boolean).length > 0 &&
                ` · ${[e.createdBy, e.createdAt ? formatDate(e.createdAt, lang) : null].filter(Boolean).join(", ")}`}
              {e.note && ` · ${e.note}`}
              {(e.shipped ?? 0) > 0 &&
                ` · ${t("warehouse.shippedPart", "od {{reserved}}, otišlo {{shipped}}", {
                  reserved: kolicina(e.quantity + (e.shipped ?? 0)),
                  shipped: kolicina(e.shipped ?? 0),
                })}`}
            </span>
          )}
          {e.mayCancel && e.id && (
            <button type="button" className="btn ghost sm" disabled={radi === e.id} onClick={() => onCancel(e)}>
              {t("warehouse.cancelReservation", "Otkaži")}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Izbor LOT-a za rezervaciju: LOT-ovi artikla sa stanja, prvo onaj koji pre ističe, sa onim što ova porudžbina sme da
 * uzme. Količina se predlaže kao ono što porudžbini još fali, koliko LOT ima slobodno za nju.
 */
function IzborLota({
  orderId,
  artikal,
  drzi,
  onDone,
  onCancel,
}: {
  orderId: string;
  artikal: Artikal;
  /** Koliko porudžbinu već pokriva: ono što se drži i ono što je otišlo. */
  drzi: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const toast = useToast();
  const [ponuda, setPonuda] = useState<LotOptions | null>(null);
  const [greska, setGreska] = useState(false);
  const [izabran, setIzabran] = useState<LotOption | null>(null);
  const [koliko, setKoliko] = useState("");
  const [napomena, setNapomena] = useState("");
  const [radi, setRadi] = useState(false);

  useEffect(() => {
    let vazi = true;
    warehouseApi
      .orderLotOptions(orderId, artikal.code)
      .then((p) => {
        if (vazi) setPonuda(p);
      })
      .catch(() => {
        if (vazi) setGreska(true);
      });
    return () => {
      vazi = false;
    };
  }, [orderId, artikal.code]);

  const kolicina = (q: number, unit: string | null) =>
    `${formatNumber(q, lang, { maximumFractionDigits: 3 })} ${unit ?? ""}`.trim();
  const broj = Number(koliko.replace(",", "."));
  const ispravno = Number.isFinite(broj) && broj > 0;

  function izaberi(o: LotOption) {
    setIzabran(o);
    // Koliko porudžbini još fali, ali ne više nego što LOT ima slobodno za nju (njeno već drži).
    const fali = Math.max(0, artikal.ordered - drzi);
    const slobodno = Math.max(0, o.available - o.heldForOrder);
    const predlog = Math.min(fali, slobodno);
    setKoliko(predlog > 0 ? String(predlog) : "");
  }

  async function rezervisi() {
    if (!izabran || !ispravno) return;
    setRadi(true);
    try {
      await warehouseApi.reserve(izabran.goodsLotId, orderId, broj, napomena.trim() || null);
      toast.success(
        t("orderHolds.reserved", "Rezervisano {{qty}} od LOT-a {{lot}}.", {
          qty: kolicina(broj, izabran.unit),
          lot: izabran.lot ?? "",
        }),
      );
      onDone();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(false);
    }
  }

  const izostavljeno = ponuda
    ? [
        ponuda.expired > 0 ? t("dispatchRelease.leftOut.expired", "isteklih {{n}}", { n: ponuda.expired }) : null,
        ponuda.unassessed > 0 ? t("dispatchRelease.leftOut.unassessed", "neocenjenih {{n}}", { n: ponuda.unassessed }) : null,
        ponuda.nonconforming > 0
          ? t("dispatchRelease.leftOut.nonconforming", "neusaglašenih {{n}}", { n: ponuda.nonconforming })
          : null,
        ponuda.domesticOnlyAbroad > 0
          ? t("orderHolds.leftOutAbroad", "SS, a kupac nije iz Srbije {{n}}", { n: ponuda.domesticOnlyAbroad })
          : null,
        ponuda.heldByOthers > 0
          ? t("dispatchRelease.leftOut.held", "zauzetih drugim porudžbinama {{n}}", { n: ponuda.heldByOthers })
          : null,
      ].filter(Boolean)
    : [];

  return (
    <div style={{ ...nested, gap: 6 }}>
      <div style={row}>
        <b style={{ color: "var(--ink)" }}>{t("dispatchRelease.fromStock", "Sa stanja magacina, prvo LOT koji pre ističe")}</b>
        {ponuda?.stockReadAt && (
          <span style={muted}>
            {t("dispatchRelease.stockReadAt", "stanje iz ERP-a {{at}}", { at: formatDateTime(ponuda.stockReadAt, lang) })}
          </span>
        )}
        {/* „Zatvori“, ne „Otkaži“: odmah iznad stoji „Otkaži“ rezervacije, a ovo samo sklapa izbor. */}
        <button type="button" className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={onCancel} disabled={radi}>
          {t("common.close", "Zatvori")}
        </button>
      </div>
      {greska ? (
        <span style={muted}>{t("dispatchRelease.stockFailed", "Stanje nije moglo da se učita; potražite LOT po oznaci.")}</span>
      ) : !ponuda ? (
        <span style={muted}>{t("dispatchRelease.loadingStock", "Čitam stanje…")}</span>
      ) : ponuda.options.length === 0 ? (
        <span style={muted}>{t("orderHolds.noneOffered", "Nijedan LOT ovog artikla sada ne može da se rezerviše.")}</span>
      ) : (
        ponuda.options.map((o) => (
          <div key={o.goodsLotId} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={row}>
              <span style={mono}>{o.lot}</span>
              {o.fields && <span style={muted}>{o.fields}</span>}
              <span style={muted}>
                <LotExpiry iso={o.expiresOn} lang={lang} />
              </span>
              <LotQualityBadge status={o.qualityStatus} note={o.qualityNote} />
              <span style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
                {t("dispatchRelease.available", "slobodno {{qty}}", { qty: kolicina(o.available, o.unit) })}
              </span>
              {o.heldForOrder > 0 && (
                <span style={muted}>
                  {t("dispatchRelease.heldForOrder", "od toga za ovu porudžbinu {{qty}}", {
                    qty: kolicina(o.heldForOrder, o.unit),
                  })}
                </span>
              )}
              <button
                type="button"
                className="btn sm"
                aria-pressed={izabran?.goodsLotId === o.goodsLotId}
                onClick={() => izaberi(o)}
                disabled={radi}
              >
                {t("dispatchRelease.pick", "Izaberi")}
              </button>
            </div>
            {izabran?.goodsLotId === o.goodsLotId && (
              <div style={{ ...row, alignItems: "center", paddingLeft: 12 }}>
                <input
                  className="input"
                  inputMode="decimal"
                  style={kolicinaPolje}
                  value={koliko}
                  onChange={(e) => setKoliko(e.target.value)}
                  placeholder={t("orderHolds.quantity", "Količina")}
                  aria-label={t("orderHolds.quantity", "Količina")}
                />
                <span style={muted}>{o.unit}</span>
                <input
                  className="input"
                  style={{ flex: 1, minWidth: 180 }}
                  value={napomena}
                  onChange={(e) => setNapomena(e.target.value)}
                  placeholder={t("orderHolds.notePlaceholder", "Napomena, npr. za prvi kamion")}
                  aria-label={t("orderHolds.note", "Napomena")}
                />
                <button type="button" className="btn primary sm" disabled={!ispravno || radi} onClick={() => void rezervisi()}>
                  {t("orderHolds.reserve", "Rezerviši")}
                </button>
              </div>
            )}
          </div>
        ))
      )}
      {izostavljeno.length > 0 && (
        <span style={muted}>
          {t("dispatchRelease.leftOut", "Nije ponuđeno:")} {izostavljeno.join(", ")}
          {ponuda && ponuda.unassessed > 0 && (
            <>
              {" · "}
              <Link to={`/warehouse?q=${encodeURIComponent(artikal.code)}&status=OPEN`}>
                {t("dispatchRelease.assessInWarehouse", "oceni u Magacinu")}
              </Link>
            </>
          )}
        </span>
      )}
    </div>
  );
}

/** Stavke porudžbine po ERP artiklu, redom stavki; stavka bez artikla (slobodan tekst) se ne drži u magacinu. */
function poArtiklu(lines: OrderLine[] | undefined): Artikal[] {
  const artikli = new Map<string, Artikal>();
  for (const l of lines ?? []) {
    if (!l.productSku) continue;
    const a = artikli.get(l.productSku);
    if (a) a.ordered += l.quantity;
    else artikli.set(l.productSku, { code: l.productSku, name: l.productName, ordered: l.quantity, unit: l.unit });
  }
  return [...artikli.values()];
}

const lista: CSSProperties = { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 };
const kolicinaPolje: CSSProperties = { width: 110, textAlign: "right" };
